import { IconChevronDownSmall } from "central-icons/IconChevronDownSmall";
import { useCallback, useEffect, useMemo, type FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { withGitpodSize } from "@/assets/icons/utils";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { Notification } from "@/components/flexkit/badge/Notification";
import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/flexkit/toast";
import { Text } from "@/components/flexkit/typography/Text";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useBranch } from "@/hooks/use-git-reference";
import { TrackLocations } from "@/hooks/use-segment";
import { type PlainAgentExecution } from "@/queries/agent-queries";
import { useDeleteEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { getDetailsURL } from "@/routes/environments/details-url";
import { GitStatusTooltipContentFromGit } from "@/routes/environments/details/GitStatusPopOver";
import { formatError } from "@/utils/errors";
import { formatChangesCount } from "@/utils/git-status";
import { isTouchDevice } from "@/utils/is-touch-device";
import { PromptInputModifierEnterKeyBinding } from "@/utils/platform";

type DeleteRunnerModalProps = {
    environment: PlainEnvironment;
    open: boolean;
    onClose: () => void;
    agentExecution?: PlainAgentExecution;
};
const EnvironmentCard: FC<{ environment: PlainEnvironment; agentExecution?: PlainAgentExecution }> = ({
    environment,
    agentExecution,
}) => {
    const environmentName = useEnvironmentName(environment, agentExecution);
    const branchName = useBranch(environment);
    const gitStatus = environment.status?.content?.git;

    const totalCommits = gitStatus?.totalUnpushedCommits || 0;
    const totalFiles = gitStatus?.totalChangedFiles || 0;
    const hasGitChanges = totalCommits > 0 || totalFiles > 0;

    const changes = totalCommits + totalFiles;

    return (
        <div className="flex w-full flex-col overflow-hidden rounded-xl border border-border-base bg-surface-secondary px-0.5 py-4">
            <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                    <EnvironmentStatusDot env={environment} size="lg" className="m-0 size-5 flex-shrink-0" />
                    <div className="flex flex-col justify-center">
                        <span className="text-base text-content-primary">{environmentName}</span>
                    </div>
                    {hasGitChanges && (
                        <>
                            <Notification content={formatChangesCount(changes)} className="rounded" />
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        type="button"
                                        className="data-[state=open]:bg-surface-hover"
                                        LeadingIcon={withGitpodSize(IconChevronDownSmall)}
                                    />
                                </PopoverTrigger>
                                <PopoverContent className="w-auto min-w-[300px] max-w-[500px]">
                                    <GitStatusTooltipContentFromGit git={gitStatus} />
                                </PopoverContent>
                            </Popover>
                        </>
                    )}
                </div>
                <span className="ml-[26px] truncate text-content-muted">{branchName}</span>
            </div>
        </div>
    );
};

export const DeleteEnvironmentModal: FC<DeleteRunnerModalProps> = ({
    environment,
    open,
    onClose,
    agentExecution,
}: DeleteRunnerModalProps) => {
    const { toast } = useToast();
    const location = useLocation();
    const navigate = useNavigate();

    const deleteEnvironment = useDeleteEnvironment();

    // Check if environment has unsaved work
    const gitStatus = environment.status?.content?.git;
    const totalCommits = gitStatus?.totalUnpushedCommits || 0;
    const totalFiles = gitStatus?.totalChangedFiles || 0;
    const hasUnsavedWork = totalCommits > 0 || totalFiles > 0;

    const warningMessage = useMemo(() => {
        if (hasUnsavedWork) {
            return "This will permanently delete the environment and all unsaved work. This action cannot be undone.";
        }
        return "This will permanently delete the environment. This action cannot be undone.";
    }, [hasUnsavedWork]);

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteEnvironment = useCallback(async () => {
        try {
            await deleteEnvironment.mutateAsync({
                environmentId: environment.id,
                force: false,
            });
            if (location.pathname == getDetailsURL({ environment: { id: environment.id } })) {
                navigate("/");
            }
            onClose();
        } catch (e) {
            toast({
                title: "Failed to delete environment",
                description: formatError(e),
            });
        }
    }, [deleteEnvironment, environment.id, toast, location.pathname, onClose, navigate]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.shiftKey && !deleteEnvironment.isPending) {
                e.preventDefault();
                void handleDeleteEnvironment();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, handleDeleteEnvironment, deleteEnvironment.isPending]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-inherit max-w-xl"
                data-track-location={TrackLocations.DeleteEnvironmentModal}
            >
                <DialogHeader>
                    <DialogTitle>Delete environment</DialogTitle>
                    <DialogDescription>{warningMessage}</DialogDescription>
                </DialogHeader>

                <DialogBody className="flex flex-col">
                    <EnvironmentCard environment={environment} agentExecution={agentExecution} />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            data-tracking-id="cancel-delete-environment-delete-environment-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteEnvironment.isPending}
                        onClick={async (e) => {
                            e.stopPropagation();
                            await handleDeleteEnvironment();
                        }}
                        data-tracking-id="confirm-delete-environment-delete-environment-modal"
                    >
                        Delete Environment
                        {!isTouchDevice() && (
                            <span className="ml-1 shrink-0 opacity-80">
                                <KeyboardCombo
                                    keys={PromptInputModifierEnterKeyBinding}
                                    className="bg-white/20 text-white"
                                />
                            </span>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const ForceDeleteEnvironmentModal: FC<DeleteRunnerModalProps> = ({
    environment,
    open,
    onClose,
    agentExecution,
}: DeleteRunnerModalProps) => {
    const { toast } = useToast();
    const location = useLocation();
    const navigate = useNavigate();

    const deleteEnvironment = useDeleteEnvironment();

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteEnvironment = useCallback(async () => {
        try {
            await deleteEnvironment.mutateAsync({
                environmentId: environment.id,
                force: true,
            });
            if (location.pathname == getDetailsURL({ environment: { id: environment.id } })) {
                navigate("/");
            }
            onClose();
        } catch (e) {
            toast({
                title: "Failed to force delete environment",
                description: formatError(e),
            });
        }
    }, [deleteEnvironment, environment.id, toast, location.pathname, onClose, navigate]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.shiftKey && !deleteEnvironment.isPending) {
                e.preventDefault();
                void handleDeleteEnvironment();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, handleDeleteEnvironment, deleteEnvironment.isPending]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-track-location={TrackLocations.ForceDeleteEnvironmentModal}>
                <DialogHeader>
                    <DialogTitle>Irreversible Action</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <EnvironmentCard environment={environment} agentExecution={agentExecution} />

                    <Text>
                        This will force delete the environment, which may leave some infrastructure or files behind that
                        require manual cleanup.
                    </Text>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            data-tracking-id="cancel-force-delete-environment-delete-environment-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteEnvironment.isPending}
                        onClick={async (e) => {
                            e.stopPropagation();
                            await handleDeleteEnvironment();
                        }}
                        data-tracking-id="confirm-force-delete-environment-delete-environment-modal"
                    >
                        Force Delete Environment
                        {!isTouchDevice() && (
                            <span className="ml-1 shrink-0 opacity-80">
                                <KeyboardCombo
                                    keys={PromptInputModifierEnterKeyBinding}
                                    className="bg-white/20 text-white"
                                />
                            </span>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
